import React, { useState } from 'react';
import { Stack, Text, Button, useBreakpoint } from '@moda/om';
import { formatPrice } from '../../lib/formatPrice';
import { useOrderSummary } from '../../hooks/useOrderSummary';
import { PaymentSummaryRow } from './PaymentSummaryRow';
import { PayTodayConfirmationModal } from './PayTodayConfirmationModal';

import './OrderPaymentSummary.scss';

interface Props {
  orderNumber: string;
}

const TAXES_TOOLTIP_COPY =
  'Taxes and duties are calculated according to the applicable laws of the state or country an item is shipped to.';

export const OrderPaymentSummary: React.FC<Props> = ({ orderNumber }) => {
  const { orderSummary } = useOrderSummary(orderNumber);
  const { matches: isDesktop } = useBreakpoint({ gt: 'md' });

  if (!orderSummary) return null;

  const {
    attributes: {
      cardTypeName,
      paymentMethod: { lastFour },
      paymentSummary: {
        costItemGiftCopy,
        discounts,
        duty,
        dutyLabel,
        payToday,
        remainingBalance,
        salesTax,
        salesTaxLabel,
        shippingAndPackaging,
        subtotal,
        total,
        totalPaid,
        vat,
        vatLabel
      },
      transactions
    }
  } = orderSummary;

  const [payTodayModalOpen, setPayTodayModalOpen] = useState(false);

  return (
    <>
      <Stack space={4}>
        <Text treatment="h3" family="serif">
          Payment Summary
        </Text>

        <div className="OrderPaymentSummary__divider" />

        <PaymentSummaryRow
          label="Order Subtotal"
          value={formatPrice(subtotal.currency, subtotal.price)}
        />

        {discounts.price > 0 && (
          <PaymentSummaryRow
            label="Gift Code"
            value={formatPrice(discounts.currency, -discounts.price)}
            tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
            tooltip={
              costItemGiftCopy
                ? 'Gift Codes are only applicable to the retail price of items in the order total. They are not applicable to any additional charges included in the order subtotal.'
                : null
            }
          />
        )}
        {duty && duty.price > 0 && (
          <PaymentSummaryRow
            label={dutyLabel}
            value={formatPrice(duty.currency, duty.price)}
            tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}
        {vat && vat.price > 0 && (
          <PaymentSummaryRow
            label={vatLabel}
            value={formatPrice(vat.currency, vat.price)}
            tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}
        {salesTax && salesTax.price > 0 && (
          <PaymentSummaryRow
            label={salesTaxLabel}
            value={formatPrice(salesTax.currency, salesTax.price)}
            tooltipAnchor={isDesktop ? 'bottomCenter' : 'bottomLeft'}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}
        <PaymentSummaryRow
          label="Shipping &amp; Packaging"
          value={formatPrice(shippingAndPackaging.currency, shippingAndPackaging.price)}
        />
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, total.price)} />

        <div className="OrderPaymentSummary__divider" />

        <Text treatment="bold1">Payment Details</Text>

        {transactions.map((transaction, index) => (
          <PaymentSummaryRow
            key={index}
            label={
              <Stack space={1} direction="horizontal" alignItems="center">
                <Text>{transaction.details}</Text>
                {transaction.source === 'amazon' && (
                  <img className="OrderPaymentSummary__logo" alt="Amazon" src="/amazon_logo.png" />
                )}
                {transaction.source === 'paypal' && (
                  <img className="OrderPaymentSummary__logo" alt="PayPal" src="/paypal.svg" />
                )}
              </Stack>
            }
            value={transaction.amount}
          />
        ))}

        {parseFloat(payToday.price.toString()) > 0 && (
          <PaymentSummaryRow
            label={`Pay Today (${cardTypeName} ${lastFour})`}
            value={formatPrice(payToday.currency, payToday.price)}
          />
        )}
        <div className="OrderPaymentSummary__divider" />

        <PaymentSummaryRow
          label="Total Paid"
          value={formatPrice(totalPaid.currency, totalPaid.price)}
        />
        {remainingBalance.price > 0 && (
          <PaymentSummaryRow
            label="Remaining Balance"
            value={formatPrice(remainingBalance.currency, remainingBalance.price)}
            tooltip="Preorder items(including Trunkshow) require a deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
          />
        )}

        {parseFloat(payToday.price.toString()) > 0 && (
          <Button onClick={() => setPayTodayModalOpen(true)}>
            {`Pay Today ${formatPrice(payToday.currency, payToday.price)}`}
          </Button>
        )}

        <Text treatment="body1" className="OrderPaymentSummary__taxes">
          **The amount of sales tax due depends on the sales tax laws of the location to which you
          are shipping
        </Text>
      </Stack>

      <PayTodayConfirmationModal
        onCancel={() => setPayTodayModalOpen(false)}
        orderNumber={orderNumber}
        show={payTodayModalOpen}
      />
    </>
  );
};
